/**
 * One shared contract for the standalone Tailor × Mannequin ADB demo.
 *
 * Keep names in Mannequin's native coordinate system. In particular the
 * powered axis is `throttle`; the UI calls it a thrust command so the demo
 * never implies that the oracle accepts dimensional thrust.
 */
export type TailorMannequinDemoAxis = {
  id: string;
  label: string;
  shortLabel: string;
  unit: string;
  min: number;
  max: number;
  held: number;
};

export type TailorMannequinDemoInput = {
  name: string;
  label: string;
  unit: string;
  min: number;
  max: number;
  default: number;
};

export const TAILOR_MANNEQUIN_DEMO_AXES: readonly TailorMannequinDemoAxis[] = [
  { id: "alpha", label: "angle of attack", shortLabel: "α", unit: "deg", min: -5, max: 24, held: 4 },
  { id: "beta", label: "sideslip", shortLabel: "β", unit: "deg", min: -8, max: 8, held: 0 },
  { id: "de", label: "elevator", shortLabel: "δe", unit: "deg", min: -12, max: 12, held: 0 },
  { id: "da", label: "aileron", shortLabel: "δa", unit: "deg", min: -18, max: 18, held: 0 },
  { id: "dr", label: "rudder", shortLabel: "δr", unit: "deg", min: -20, max: 20, held: 0 },
  { id: "Mach", label: "Mach", shortLabel: "M", unit: "", min: 0.08, max: 0.28, held: 0.15 },
  { id: "throttle", label: "thrust command", shortLabel: "T/Tmax", unit: "fraction", min: 0, max: 1, held: 0.35 },
] as const;

export const TAILOR_MANNEQUIN_DEMO_AXIS_NAMES = TAILOR_MANNEQUIN_DEMO_AXES.map((axis) => axis.id);
export const TAILOR_MANNEQUIN_INITIAL_DESIGN_SIZE = 56;
/** Tailor conditions its dense ARD posterior on every observation through the
 * first 128 selections. Beyond this point the runtime advances with a bounded
 * active fit set while retaining real oracle responses for every condition. */
export const TAILOR_MANNEQUIN_TRAINING_BUDGET = 128;
export const TAILOR_MANNEQUIN_MAX_EXACT_BUDGET = 128;
/** Default and maximum real Tailor campaign size in the recording demo. */
export const TAILOR_MANNEQUIN_DEFAULT_DATABASE_TARGET = 10_000;
export const TAILOR_MANNEQUIN_MAX_DATABASE_TARGET = 10_000;
/** Surface frames are intentionally much sparser than solve points. The first
 * 128 tell the point-by-point story; large campaigns then show meaningful
 * logarithmic milestones without trying to hydrate thousands of plots. */
export const TAILOR_MANNEQUIN_REPLAY_CHECKPOINTS = [
  8, 16, 24, 40, 56, 64, 80, 96, 112, 128,
  250, 500, 1_000, 2_500, 5_000, 10_000,
] as const;

/** Keep every seed item, then take an even deterministic sample of the
 * adaptive history plus the newest item. The caller's full array remains the
 * source of campaign counts and posterior requests. */
export function thinTailorReplayItems<T>(
  items: T[],
  maxItems: number,
  seedSize = TAILOR_MANNEQUIN_INITIAL_DESIGN_SIZE,
): T[] {
  if (items.length <= maxItems) return items;
  const headCount = Math.min(seedSize, Math.max(1, maxItems - 1), items.length);
  const head = items.slice(0, headCount);
  const tail = items.slice(headCount);
  const slots = Math.max(1, maxItems - head.length);
  if (tail.length <= slots) return [...head, ...tail];
  const sampled = Array.from({ length: slots }, (_, index) => {
    const position = slots === 1
      ? tail.length - 1
      : Math.round((index * (tail.length - 1)) / (slots - 1));
    return tail[position]!;
  });
  return [...head, ...sampled];
}

export function nextTailorReplayCount(current: number, available: number, budget: number): number {
  const cappedAvailable = Math.min(Math.max(0, available), budget);
  if (current >= cappedAvailable) return current;
  if (current < Math.min(TAILOR_MANNEQUIN_TRAINING_BUDGET, budget)) {
    return Math.min(current + 1, cappedAvailable);
  }
  const nextCheckpoint = [...TAILOR_MANNEQUIN_REPLAY_CHECKPOINTS, budget]
    .filter((checkpoint) => checkpoint > current && checkpoint <= cappedAvailable)
    .sort((left, right) => left - right)[0];
  return nextCheckpoint ?? (cappedAvailable >= budget ? budget : current);
}

export function defaultTailorMannequinInputs(dimensions = TAILOR_MANNEQUIN_DEMO_AXES.length): TailorMannequinDemoInput[] {
  const count = Math.max(1, Math.min(TAILOR_MANNEQUIN_DEMO_AXES.length, Math.trunc(dimensions)));
  return TAILOR_MANNEQUIN_DEMO_AXES.slice(0, count).map((axis) => ({
    name: axis.id,
    label: axis.label,
    unit: axis.unit,
    min: axis.min,
    max: axis.max,
    default: axis.held,
  }));
}

export function tailorMannequinAxis(name: string): TailorMannequinDemoAxis | undefined {
  return TAILOR_MANNEQUIN_DEMO_AXES.find((axis) => axis.id === name);
}
